import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Save,
  Eye,
  ExternalLink,
  Loader2,
  Undo2,
  LayoutTemplate,
} from "lucide-react";
import { toast } from "sonner";

import BlockList, { BLOCK_LABELS } from "@/components/builder/BlockList";
import BlockEditor from "@/components/builder/BlockEditor";
import BlockPreview from "@/components/builder/BlockPreview";
import TemplateGallery from "@/components/builder/TemplateGallery";
import { TEMPLATE_DEFAULTS, buildBlocksFromTemplate } from "@/components/builder/templates";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const UNDO_LIMIT = 10;

function newId() {
  return `b_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function makeBlock(type, defaults) {
  const base = TEMPLATE_DEFAULTS[type] || defaults || {};
  return {
    id: newId(),
    type,
    data: { ...base },
  };
}

export default function PageBuilderPage() {
  const { businessId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [business, setBusiness] = useState(null);
  const [blockTypes, setBlockTypes] = useState({});
  const [blocks, setBlocks] = useState([]);
  const [theme, setTheme] = useState({ primary: "#10b981" });
  const [selectedId, setSelectedId] = useState(null);
  const [addBlockType, setAddBlockType] = useState("");
  const [templateOpen, setTemplateOpen] = useState(false);

  // Historia (undo stack) — przechowuje POPRZEDNIE stany blocks/theme.
  // Limit do UNDO_LIMIT poziomow.
  const historyRef = useRef([]);
  const skipHistoryRef = useRef(false);

  const pushHistory = useCallback((prevBlocks, prevTheme) => {
    historyRef.current = [
      ...historyRef.current,
      { blocks: prevBlocks, theme: prevTheme },
    ].slice(-UNDO_LIMIT);
  }, []);

  // Wrapper: dowolna mutacja blokow / motywu zapisuje stan w historii.
  const applyMutation = useCallback(
    (mutator) => {
      pushHistory(blocks, theme);
      const result = mutator(blocks, theme);
      if (result?.blocks !== undefined) setBlocks(result.blocks);
      if (result?.theme !== undefined) setTheme(result.theme);
    },
    [blocks, theme, pushHistory]
  );

  const undo = () => {
    const stack = historyRef.current;
    if (!stack.length) return;
    const prev = stack[stack.length - 1];
    historyRef.current = stack.slice(0, -1);
    skipHistoryRef.current = true;
    setBlocks(prev.blocks);
    setTheme(prev.theme);
    toast.success("Cofnieto ostatnia zmiane");
  };

  // ============ Initial load ============
  useEffect(() => {
    (async () => {
      try {
        const [biz, types, builder] = await Promise.all([
          axios.get(`${API}/businesses/${businessId}`),
          axios.get(`${API}/builder/block-types`),
          axios.get(`${API}/landing-pages/${businessId}/builder`),
        ]);
        setBusiness(biz.data);
        setBlockTypes(types.data.types || {});
        if (builder.data.blocks?.length) {
          setBlocks(builder.data.blocks);
          setSelectedId(builder.data.blocks[0]?.id);
        } else {
          // sensowny default: hero + about + slotify (gdy firma ma slotify_slug) + contact + opening + map
          const defaults = [
            makeBlock("hero", { title: biz.data.name, subtitle: biz.data.description?.slice(0, 80) || "" }),
            makeBlock("about", { body: biz.data.description || "" }),
          ];
          if (biz.data.slotify_slug) {
            const sb = makeBlock("slotify");
            sb.data.slug = biz.data.slotify_slug;
            sb.data.subheading = "Wybierz uslugodawce, date i godzine. Potwierdzenie na e-mail w kilka sekund.";
            defaults.push(sb);
          }
          defaults.push(makeBlock("contact"));
          defaults.push(makeBlock("opening"));
          defaults.push(makeBlock("map"));
          setBlocks(defaults);
          setSelectedId(defaults[0].id);
        }
        if (builder.data.theme?.primary) setTheme(builder.data.theme);
      } catch (e) {
        toast.error("Nie udalo sie zaladowac");
        navigate("/");
      } finally {
        setLoading(false);
      }
    })();
  }, [businessId, navigate]);

  // ============ Block ops ============
  const moveBlock = (idx, delta) => {
    applyMutation((bs) => {
      const next = [...bs];
      const target = idx + delta;
      if (target < 0 || target >= next.length) return { blocks: bs };
      [next[idx], next[target]] = [next[target], next[idx]];
      return { blocks: next };
    });
  };

  const deleteBlock = (id) => {
    applyMutation((bs) => ({ blocks: bs.filter((b) => b.id !== id) }));
    if (selectedId === id) setSelectedId(null);
  };

  const updateBlock = (updated) => {
    applyMutation((bs) => ({ blocks: bs.map((b) => (b.id === updated.id ? updated : b)) }));
  };

  const addBlock = () => {
    if (!addBlockType) return;
    const defaults = blockTypes[addBlockType] || TEMPLATE_DEFAULTS[addBlockType] || {};
    const nb = makeBlock(addBlockType, defaults);
    applyMutation((bs) => ({ blocks: [...bs, nb] }));
    setSelectedId(nb.id);
    setAddBlockType("");
  };

  const applyTemplate = (key) => {
    const nextBlocks = buildBlocksFromTemplate(key);
    applyMutation(() => ({ blocks: nextBlocks }));
    setSelectedId(nextBlocks[0]?.id || null);
    toast.success("Szablon zastosowany");
  };

  const setThemePrimary = (v) => {
    applyMutation((_, t) => ({ theme: { ...t, primary: v } }));
  };

  const setThemeMode = (v) => {
    applyMutation((_, t) => ({ theme: { ...t, mode: v ? "dark" : "light" } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/landing-pages/${businessId}/builder`,
        { blocks, theme },
        { withCredentials: true }
      );
      toast.success("Zapisano");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Blad zapisu");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const subdomainUrl = business?.subdomain
    ? `https://${business.subdomain}.${window.location.hostname.replace(/^[^.]+\./, "")}/`
    : null;
  const selected = blocks.find((b) => b.id === selectedId);
  const canUndo = historyRef.current.length > 0;

  // Lista wszystkich typow do dodawania (frontend + backend + custom).
  const allTypes = Object.keys({ ...TEMPLATE_DEFAULTS, ...(blockTypes || {}) });

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b sticky top-0 z-20">
        <div className="max-w-[1600px] mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/dashboard" className="text-sm text-slate-500 hover:text-slate-900">
              &larr; Panel
            </Link>
            <div className="min-w-0">
              <div className="font-semibold truncate">{business?.name}</div>
              <div className="text-xs text-slate-500">WYSIWYG builder strony</div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => setTemplateOpen(true)}>
              <LayoutTemplate className="w-4 h-4 mr-2" />
              Szablony
            </Button>
            <Button variant="outline" size="sm" onClick={undo} disabled={!canUndo}>
              <Undo2 className="w-4 h-4 mr-2" />
              Cofnij
            </Button>
            {subdomainUrl && (
              <a href={subdomainUrl} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm">
                  <Eye className="w-4 h-4 mr-2" />
                  Podglad
                  <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
              </a>
            )}
            <Button
              onClick={save}
              disabled={saving}
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Zapisywanie
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" /> Zapisz
                </>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Layout 50/50 — lewy panel: edycja, prawy: live preview */}
      <div className="max-w-[1600px] mx-auto px-4 py-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* LEWA KOLUMNA — lista blokow, edytor wybranego, dodawanie */}
        <div className="space-y-3">
          <BlockList
            blocks={blocks}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onMove={moveBlock}
            onDelete={deleteBlock}
          />

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Dodaj blok</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Select value={addBlockType} onValueChange={setAddBlockType}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Wybierz typ bloku" />
                  </SelectTrigger>
                  <SelectContent>
                    {allTypes.map((t) => (
                      <SelectItem key={t} value={t}>
                        {BLOCK_LABELS[t] || t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={addBlock} disabled={!addBlockType} variant="outline">
                  <Plus className="w-4 h-4 mr-1" />
                  Dodaj
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {selected ? `Edycja: ${BLOCK_LABELS[selected.type] || selected.type}` : "Edytor bloku"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selected ? (
                <BlockEditor block={selected} onChange={updateBlock} />
              ) : (
                <p className="text-sm text-slate-500">
                  Wybierz blok z listy powyzej lub dodaj nowy. Zmiany pokazuja sie od razu po prawej stronie.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Motyw</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <Label htmlFor="primary" className="text-sm">
                  Kolor wiodacy
                </Label>
                <input
                  id="primary"
                  type="color"
                  value={theme.primary || "#10b981"}
                  onChange={(e) => setThemePrimary(e.target.value)}
                  className="w-12 h-9 rounded border border-slate-200"
                />
                <span className="text-xs text-slate-500 font-mono">{theme.primary}</span>
              </div>
              <label className="flex items-center gap-2 mt-4">
                <Checkbox checked={theme.mode === "dark"} onCheckedChange={setThemeMode} />
                <span className="text-sm">Ciemny motyw calej strony</span>
              </label>
              <p className="text-xs text-slate-500 mt-1">
                Przelacza tlo strony i bloki (widget, kontakt, mapa) na ciemny, spojny wyglad. Domyslnie wylaczone.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* PRAWA KOLUMNA — live preview */}
        <div className="lg:sticky lg:top-20 lg:self-start lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
              Podglad na zywo
            </span>
            <span className="text-xs text-slate-400">
              {blocks.length} {blocks.length === 1 ? "blok" : "blokow"}
            </span>
          </div>
          <BlockPreview
            blocks={blocks}
            business={business}
            theme={theme}
            selectedId={selectedId}
            onSelectBlock={setSelectedId}
          />
        </div>
      </div>

      <TemplateGallery
        open={templateOpen}
        onOpenChange={setTemplateOpen}
        onApply={applyTemplate}
        hasExistingBlocks={blocks.length > 0}
      />
    </div>
  );
}
